import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import * as moment from 'moment';
import {BehaviorSubject, filter, map, Observable, take, withLatestFrom} from 'rxjs';
import {Alert} from '../core/model/alert';
import {Project} from '../core/model/project';
import {RewardCoin} from '../core/model/reward';
import {User} from '../core/model/user';
import {selectActiveUser} from '../core/store/active-user/active-user.selector';
import {DismissAlertAction, GetAlertsAction, GetAlertsCountAction} from '../core/store/alerts/alerts.action';
import {selectAllAlerts} from '../core/store/alerts/alerts.selector';
import {GetProjectsAction} from '../core/store/projects/projects.action';
import {selectProjectsMap} from '../core/store/projects/projects.selector';
import {GetCoinTypesAction} from '../core/store/rewards/rewards.action';
import {selectCoinTypesMap} from '../core/store/rewards/rewards.selector';
import {selectUsersMap} from '../core/store/users/users.selector';
import {OphIconModule} from '../shared/design/oph-icon/oph-icon.module';
import {OphLoadingModule} from '../shared/design/oph-loading/oph-loading.module';
import {PipesModule} from '../shared/pipes/pipes.module';
import {AlertsActiveService} from '../shared/services/alerts-active.service';
import {AlertTypes} from './shared/alert-types';
import {MessageService} from '../services/message.service';
import {TitleService} from '../core/page/title.service';

interface AlertDisplay {
  icon: string;
  name: string;
  text: string;
  timeAgo: string;
  dismissed: boolean;
  type?: 'project' | 'task' | 'triggeredTask';
  projectId?: string;
  taskId?: string;
  linkable?: boolean;
  linkText?: string;
}

@Component({
  selector: 'alerts',
  standalone: true,
  imports: [OphIconModule, PipesModule, AsyncPipe, OphLoadingModule],
  templateUrl: './alerts.component.html',
  styleUrl: './alerts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertsComponent implements OnInit, OnDestroy {
  @HostBinding('class') hostClasses = 'oph-feature-layout oph-feature-padding';

  alerts$: Observable<Alert[]> = this.store$.select(selectAllAlerts);
  formattedAlerts$: Observable<AlertDisplay[]>;
  usersMap$: Observable<Record<string, User>> = this.store$.select(selectUsersMap);
  activeUser$: Observable<User> = this.store$.select(selectActiveUser);
  coinTypesMap$: Observable<Record<string, RewardCoin>> = this.store$.select(selectCoinTypesMap);
  projectsMap$: Observable<Record<string, Project>> = this.store$.select(selectProjectsMap);

  loadingAlerts$ = new BehaviorSubject<boolean>(true);
  private refreshInterval: ReturnType<typeof setInterval>;
  private refreshIntervalMinutes = 1;

  constructor(
    private store$: Store,
    private alertsActiveService: AlertsActiveService,
    private router: Router,
    private messageService: MessageService,
    private titleService: TitleService
  ) {}

  ngOnInit() {
    this.titleService.setPageTitle('Alerts');
    // Notify that alerts component is active
    this.alertsActiveService.setAlertsActive(true);

    this.store$.dispatch(new GetCoinTypesAction({}));
    this.store$.dispatch(new GetProjectsAction({}));
    this.fetchAlertsData();
    this.formattedAlerts$ = this.observeAlerts();

    // Start our own refresh interval
    this.startRefreshInterval();
  }

  ngOnDestroy() {
    // Notify that alerts component is no longer active
    this.alertsActiveService.setAlertsActive(false);

    // Clean up the interval
    this.stopRefreshInterval();

    // Update the count one last time before leaving
    this.store$.dispatch(new GetAlertsCountAction());
  }

  private startRefreshInterval(): void {
    this.stopRefreshInterval();

    // Set up periodic refresh of alerts data
    this.refreshInterval = setInterval(() => {
      this.fetchAlertsData();
    }, this.refreshIntervalMinutes * 60000);
  }

  private stopRefreshInterval(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  private fetchAlertsData(): void {
    this.store$.dispatch(new GetAlertsCountAction());
    this.store$.dispatch(
      new GetAlertsAction({
        onSuccess: () => {
          setTimeout(() => this.loadingAlerts$.next(false), 500);
        },
        onFailure: () => this.loadingAlerts$.next(false),
      })
    );
  }

  observeAlerts(): Observable<AlertDisplay[]> {
    return this.alerts$.pipe(
      filter(alerts => !!alerts),
      withLatestFrom(this.usersMap$, this.coinTypesMap$, this.projectsMap$, this.activeUser$),
      map(([alerts, usersMap, coinTypesMap, projectsMap, activeUser]) =>
        alerts.map(alert => {
          const baseAlert = {
            timeAgo: this.getTimeAgo(alert.timestamp),
            dismissed: alert.subscribedUsers[0]?.dismissed || false,
          };

          let coinType: RewardCoin | undefined;
          const firstName = usersMap[alert.userId]?.firstName;
          const lastInitial = usersMap[alert.userId]?.lastName[0] || '';
          const userName =
            activeUser?._id === alert.userId
              ? 'You'
              : `${firstName} ${firstName === 'admin' ? '' : lastInitial[0] + '.'}`;

          switch (alert.action) {
            case AlertTypes.Custom:
              return {
                ...baseAlert,
                icon: 'custom',
                name: alert.details?.alertName,
                text: alert.details?.alertText,
              };
            case AlertTypes.ProjectExpiringSoon:
              return {
                ...baseAlert,
                icon: 'alert',
                name: 'Project Ending Soon',
                text: `Expires in ${moment.duration(alert.details.remainingTime).humanize()}`,
                type: 'project',
                link: alert.details.projectId ? `projects/${alert.details.projectId}` : undefined,
                linkText: alert.details.projectLabel || 'View Project',
                linkable: alert.linkable || null,
                projectId: alert.details.projectId,
              };
            case AlertTypes.TaskTriggered:
              return {
                ...baseAlert,
                icon: 'triggered',
                name: 'Task Triggered',
                text: alert.details?.taskName,
                type: 'triggeredTask',
                linkText: alert.details?.taskName || 'View Task',
                linkable: alert.linkable || null,
                taskId: alert.details.taskId,
                projectId: alert.details.projectId,
              };
            case AlertTypes.TaskCompleted:
              return {
                ...baseAlert,
                icon: 'completed',
                name: 'Task Completed',
                text: `${userName} completed `,
                type: 'task',
                linkText: alert.details?.taskName || 'View Task',
                linkable: alert.linkable || null,
                taskId: alert.details.taskId,
                projectId: alert.details.projectId,
              };
            case AlertTypes.TaskCancelled:
              return {
                ...baseAlert,
                icon: 'cancelled',
                name: 'Task Cancelled',
                text: `${userName} cancelled `,
                type: 'task',
                linkText: alert.details?.taskName || 'View Task',
                linkable: alert.linkable || null,
                taskId: alert.details.taskId,
                projectId: alert.details.projectId,
              };
            case AlertTypes.TaskStarted:
              return {
                ...baseAlert,
                icon: 'started',
                name: 'Task Started',
                text: `${userName} started `,
                type: 'task',
                linkText: alert.details?.taskName || 'View Task',
                linkable: alert.linkable || null,
                taskId: alert.details.taskId,
                projectId: alert.details.projectId,
              };
            case AlertTypes.TaskPutOnHold:
              return {
                ...baseAlert,
                icon: 'pause',
                name: 'Task Put On Hold',
                text: `${userName} put on hold `,
                type: 'task',
                linkText: alert.details?.taskName || 'View Task',
                linkable: alert.linkable || null,
                taskId: alert.details.taskId,
                projectId: alert.details.projectId,
              };
            case AlertTypes.TokenEarned:
              return {
                ...baseAlert,
                icon: 'token',
                name: 'Token Earned',
                text: `${String(projectsMap[alert.details.projectId]?.label || '(Unknown Project)')} earned "${
                  alert.details?.tokenName || '(No token name)'
                }"`,
              };
            case AlertTypes.CoinsEarned:
              coinType = alert.details?.coinType ? coinTypesMap[alert.details.coinType] : undefined;
              return {
                ...baseAlert,
                icon: 'reward',
                name: 'Coins Earned',
                text: `${userName} earned ${alert.details?.coinValue} ${(
                  coinType?.name || '(No token name)'
                ).toLowerCase()}`,
              };
            case AlertTypes.MetricUpdated:
              return {
                ...baseAlert,
                icon: 'metric',
                name: 'Metric Updated',
                text: 'Metric Updated',
              };
            default:
              return {
                ...baseAlert,
                icon: 'custom',
                name: 'Unknown Alert',
                text: 'Unknown alert type',
              };
          }
        })
      )
    );
  }

  getTimeAgo(timestamp: string): string {
    const date = moment(timestamp);
    const now = moment();

    // If date is in the future or within last few seconds, return "now"
    if (date.isAfter(now) || now.diff(date, 'seconds') < 5) {
      return 'now';
    }

    return date.fromNow();
  }

  onAlertClick(alert: Alert) {
    this.store$.dispatch(new DismissAlertAction({alertId: alert._id}));
  }

  onLinkClick(alert: AlertDisplay) {
    this.projectsMap$.pipe(take(1)).subscribe(projectsMap => {
      const project = projectsMap[alert.projectId];
      if (!project) {
        this.messageService.add('Project not found', 'error');
        return;
      }

      sessionStorage.setItem('ophStoredProjectId', project._id);
      if (alert.type === 'project') {
        this.router.navigate(['projects', project._id], {queryParams: {returnTo: this.router.url}});
        return;
      }
      if (alert.type === 'task') {
        this.router.navigate(['projects', project._id, 'task', alert.taskId], {
          queryParams: {returnTo: this.router.url},
        });
      }
    });
  }
}
